/* eslint-disable no-unused-vars */
import React, { useContext, useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'underscore';
import { Dropdown, TextField, FontIcon } from '@fluentui/react';
import { I18nContext, withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { HelpButton } from '@bingads-webui-react/help-popup';
import { GoalCreationWizardContext } from '../goal-creation-wizard-context';
import { getStepStyles } from '../../../conversion-wizard-components/src/shared-styles/shared-content-style';
import { cali18n } from '../shared/contants';
import { getExpressionOperatorOptions, getValueOperatorOptions } from './goal-creation-options';
import { validateNumeric, validateString } from '../shared/validation-util';

const EventParamSettingUnstyled = (props) => {
  const {
    classes,
    goalObj,
    updateGoalProps,
    showError,
    resetShowError,
  } = props;
  const i18n = useContext(I18nContext);
  const wizardContext = useContext(GoalCreationWizardContext);
  const {
    CategoryOperator,
    Category,
    ActionOperator,
    Action,
    LabelOperator,
    Label,
    ValueOperator,
    Value,
  } = goalObj;
  const [categoryOperator, setCategoryOperator] = useState(CategoryOperator === 'NoExpression' ? 'EqualsTo' : CategoryOperator);
  const [categoryValue, setCategoryValue] = useState(_.isNull(Category) ? '' : Category);
  const [actionOperator, setActionOperator] = useState(ActionOperator === 'NoExpression' ? 'EqualsTo' : ActionOperator);
  const [actionValue, setActionValue] = useState(_.isNull(Action) ? '' : Action);
  const [labelOperator, setLabelOperator] = useState(LabelOperator === 'NoExpression' ? 'EqualsTo' : LabelOperator);
  const [labelValue, setLabelValue] = useState(_.isNull(Label) ? '' : Label);
  const [valueOperator, setValueOperator] = useState(ValueOperator === 'NoValue' ? 'EqualTo' : ValueOperator);
  const [valueValue, setValueValue] = useState(_.isNull(Value) ? '' : Value.toString());
  const expressionOperatorOptions = useMemo(() => getExpressionOperatorOptions(i18n), [i18n]);
  const valueOperatorOptions = useMemo(() => getValueOperatorOptions(i18n), [i18n]);

  const [inputVisibleStates, setInputVisibleStates] = useState({
    Action: !_.isEmpty(actionValue) || (_.isEmpty(valueValue) && _.isEmpty(labelValue) && _.isEmpty(categoryValue)),
    Value: !_.isEmpty(valueValue),
    Label: !_.isEmpty(labelValue),
    Category: !_.isEmpty(categoryValue),
  });

  const [errorMsg, setErrorMsg] = useState('');
  const [categoryErrMessage, setCategoryErrMessage] = useState('');
  const [actionErrMessage, setActionErrMessage] = useState('');
  const [labelErrMessage, setLabelErrMessage] = useState('');
  const [valueErrMessage, setValueErrMessage] = useState('');

  const getErrorMessage = useCallback(() => {
    if (!Category && !Action && !Label && !Value) {
      return i18n.getString(_TL_('Enter at least one parameter to create an event goal.'));
    }
    return '';
  }, [Action, Category, Label, Value, i18n]);

  const getStringErrorMessage = useCallback((value) => {
    if (!validateString(value)) {
      return i18n.getString(_TL_('Field contains invalid characters'));
    }
    return '';
  }, [i18n]);

  const getValueErrorMessage = useCallback((value) => {
    const parsedValue = parseInt(value, 10);
    if (!_.isNaN(parsedValue) && parsedValue > 9.22e14) {
      return i18n.getString(_TL_('Enter a valid value'));
    }
    return '';
  }, [i18n]);

  useEffect(() => {
    if (showError) {
      setErrorMsg(getErrorMessage);
      resetShowError();
    }
  }, [getErrorMessage, resetShowError, showError]);

  const onChangeCategoryOperator = useCallback((event, option) => {
    setCategoryOperator(option.key);
    updateGoalProps({ CategoryOperator: option.key });
  }, [updateGoalProps]);

  const onChangeCategoryValue = useCallback((event, value) => {
    setCategoryValue(value);
    setCategoryErrMessage(getStringErrorMessage(value));
    updateGoalProps({ CategoryOperator: categoryOperator, Category: value.trim().length > 0 ? value.trim() : null });
  }, [categoryOperator, getStringErrorMessage, updateGoalProps]);

  const onChangeActionOperator = useCallback((event, option) => {
    setActionOperator(option.key);
    updateGoalProps({ ActionOperator: option.key });
  }, [updateGoalProps]);

  const onChangeActionValue = useCallback((event, value) => {
    setActionValue(value);
    setActionErrMessage(getStringErrorMessage(value));
    updateGoalProps({ ActionOperator: actionOperator, Action: value.trim().length > 0 ? value.trim() : null });
  }, [actionOperator, getStringErrorMessage, updateGoalProps]);

  const onChangeLabelOperator = useCallback((event, option) => {
    setLabelOperator(option.key);
    updateGoalProps({ LabelOperator: option.key });
  }, [updateGoalProps]);

  const onChangeLabelValue = useCallback((event, value) => {
    setLabelValue(value);
    setLabelErrMessage(getStringErrorMessage(value));
    updateGoalProps({ LabelOperator: labelOperator, Label: value.trim().length > 0 ? value.trim() : null });
  }, [getStringErrorMessage, labelOperator, updateGoalProps]);

  const onChangeValueOperator = useCallback((event, option) => {
    setValueOperator(option.key);
    updateGoalProps({ ValueOperator: option.key });
  }, [updateGoalProps]);

  const onChangeValueValue = useCallback((event, value) => {
    if ((validateNumeric(value) || value.length === 0) && value.length <= 15) {
      setValueValue(value);
      setValueErrMessage(getValueErrorMessage(value));
      const parsedValue = parseInt(value, 10);
      updateGoalProps({ ValueOperator: valueOperator, Value: _.isNaN(parsedValue) || parsedValue > 9.22e14 ? null : parsedValue });
    }
  }, [getValueErrorMessage, updateGoalProps, valueOperator]);

  const onClearActionParam = useCallback((event) => {
    setActionOperator('EqualsTo');
    setActionValue('');
    setActionErrMessage('');
    setInputVisibleStates({ ...inputVisibleStates, Action: false });
    updateGoalProps({ ActionOperator: 'NoExpression', Action: null });
  }, [inputVisibleStates, updateGoalProps]);

  const onClearCategoryParam = useCallback((event) => {
    setCategoryOperator('EqualsTo');
    setCategoryValue('');
    setCategoryErrMessage('');
    setInputVisibleStates({ ...inputVisibleStates, Category: false });
    updateGoalProps({ CategoryOperator: 'NoExpression', Category: null });
  }, [inputVisibleStates, updateGoalProps]);

  const onClearLabelParam = useCallback((event) => {
    setLabelOperator('EqualsTo');
    setLabelValue('');
    setLabelErrMessage('');
    setInputVisibleStates({ ...inputVisibleStates, Label: false });
    updateGoalProps({ LabelOperator: 'NoExpression', Label: null });
  }, [inputVisibleStates, updateGoalProps]);

  const onClearValueParam = useCallback((event) => {
    setValueOperator('EqualTo');
    setValueValue('');
    setValueErrMessage('');
    setInputVisibleStates({ ...inputVisibleStates, Value: false });
    updateGoalProps({ ValueOperator: 'NoValue', Value: null });
  }, [inputVisibleStates, updateGoalProps]);

  const onChangeDropdownValue = useCallback((event, option) => {
    setInputVisibleStates({ ...inputVisibleStates, [option.key]: true });
  }, [inputVisibleStates]);

  const onKeyEnter = useCallback(callback => (event) => {
    if (event.key === 'Enter') {
      callback(event);
    }
  }, []);

  const addAnotherParamOptions = useMemo(() => _.filter([
    { key: 'Action', text: i18n.getString(_TL_(('Action'))) },
    { key: 'Label', text: i18n.getString(_TL_(('Label'))) },
    { key: 'Value', text: i18n.getString(_TL_(('Value'))) },
    { key: 'Category', text: i18n.getString(_TL_(('Category'))) },
  ], option => !inputVisibleStates[option.key]), [i18n, inputVisibleStates]);

  const onRenderTitleForAddOperator = () => (
    <React.Fragment>
      <FontIcon iconName="Add" className={classes.dropdownAddIcon} />
      <span className={classes.title}>{i18n.getString(_TL_('Add another parameter'))}</span>
    </React.Fragment>);

  return (
    <div className={classes.eventParamSettingContainer}>
      <div className={classes.nonAccordionTitle}>
        <span className="title">{i18n.getString(cali18n('Event_Goal_Settings_Custom_Event'))}</span>
        <HelpButton
          aria-label={i18n.getString(cali18n('Learn_More'))}
          topic="pop_BA_UET_CustomEvent"
        />
      </div>
      <p className={classes.bodyText}>{i18n.getString(cali18n('Event_Goal_Settings_Event_Param_Instruction'))}
        <button
          className="HelpTopic clickable btn-link"
          query="help:app51208"
          aria-label={i18n.getString(cali18n('Learn_More'))}
        >
          {i18n.getString(_TL_('Learn more'))}
        </button>
      </p>
      <div className={classNames('row', inputVisibleStates.Action ? '' : 'hide')}>
        <span className="col-sm-1" id="action-operator-text">{i18n.getString(_TL_('Action'))}</span>
        <Dropdown
          className="col-sm-2"
          selectedKey={actionOperator}
          options={expressionOperatorOptions}
          onChange={onChangeActionOperator}
          aria-labelledby="action-operator-text"
        />
        <TextField
          className="col-sm-2"
          value={actionValue}
          onChange={onChangeActionValue}
        />
        <FontIcon
          tabIndex="0"
          role="button"
          aria-label={i18n.getString(_TL_('Clear action parameter'))}
          iconName="Cancel"
          className={classNames(classes.eventParamRemoveIcon, 'col-sm-1')}
          onClick={onClearActionParam}
          onKeyDown={onKeyEnter(onClearActionParam)}
        />
        {!_.isEmpty(actionErrMessage) &&
          <div role="alert">
            <p className={classes.errorText}>{actionErrMessage}</p>
          </div>}
      </div>
      <div className={classNames('row', inputVisibleStates.Category ? '' : 'hide')}>
        <span className="col-sm-1">{i18n.getString(_TL_('Category'))}</span>
        <Dropdown
          className="col-sm-2"
          selectedKey={categoryOperator}
          options={expressionOperatorOptions}
          onChange={onChangeCategoryOperator}
        />
        <TextField
          className="col-sm-2"
          value={categoryValue}
          onChange={onChangeCategoryValue}
        />
        <FontIcon
          tabIndex="0"
          role="button"
          aria-label={i18n.getString(_TL_('Clear category parameter'))}
          iconName="Cancel"
          className={classNames(classes.eventParamRemoveIcon, 'col-sm-1')}
          onClick={onClearCategoryParam}
          onKeyDown={onKeyEnter(onClearCategoryParam)}
        />
        {!_.isEmpty(categoryErrMessage) &&
          <div role="alert">
            <p className={classes.errorText}>{categoryErrMessage}</p>
          </div>}
      </div>
      <div className={classNames('row', inputVisibleStates.Label ? '' : 'hide')}>
        <span className="col-sm-1">{i18n.getString(_TL_('Label'))}</span>
        <Dropdown
          className="col-sm-2"
          selectedKey={labelOperator}
          options={expressionOperatorOptions}
          onChange={onChangeLabelOperator}
        />
        <TextField
          className="col-sm-2"
          value={labelValue}
          onChange={onChangeLabelValue}
        />
        <FontIcon
          tabIndex="0"
          role="button"
          aria-label={i18n.getString(_TL_('Clear label parameter'))}
          iconName="Cancel"
          className={classNames(classes.eventParamRemoveIcon, 'col-sm-1')}
          onClick={onClearLabelParam}
          onKeyDown={onKeyEnter(onClearLabelParam)}
        />
        {!_.isEmpty(labelErrMessage) &&
          <div role="alert">
            <p className={classes.errorText}>{labelErrMessage}</p>
          </div>}
      </div>
      <div className={classNames('row', inputVisibleStates.Value ? '' : 'hide')}>
        <span className="col-sm-1">{i18n.getString(_TL_('Value'))}</span>
        <Dropdown
          className="col-sm-2"
          selectedKey={valueOperator}
          options={valueOperatorOptions}
          onChange={onChangeValueOperator}
        />
        <TextField
          className="col-sm-2"
          value={valueValue}
          onChange={onChangeValueValue}
        />
        <FontIcon
          tabIndex="0"
          role="button"
          aria-label={i18n.getString(_TL_('Clear value parameter'))}
          iconName="Cancel"
          className={classNames(classes.eventParamRemoveIcon, 'col-sm-1')}
          onClick={onClearValueParam}
          onKeyDown={onKeyEnter(onClearValueParam)}
        />
        {!_.isEmpty(valueErrMessage) &&
          <div role="alert">
            <p className={classes.errorText}>{valueErrMessage}</p>
          </div>}
      </div>
      {addAnotherParamOptions.length > 0 && <Dropdown
        className="add-param-dropdown"
        onRenderTitle={onRenderTitleForAddOperator}
        onRenderPlaceholder={onRenderTitleForAddOperator}
        onChange={onChangeDropdownValue}
        defaultSelectedKey={null}
        options={addAnotherParamOptions}
        styles={{ dropdown: { width: 250 } }}
      />}
      {!_.isEmpty(errorMsg) &&
        <div className="row" role="alert">
          <p className={classes.errorText}>{errorMsg}</p>
        </div>}
    </div>
  );
};

EventParamSettingUnstyled.propTypes = {
  classes: PropTypes.shape(_.mapObject(
    getStepStyles(),
    _.constant(PropTypes.string.isRequired)
  )).isRequired,
  goalObj: PropTypes.shape({}).isRequired,
  updateGoalProps: PropTypes.func.isRequired,
  showError: PropTypes.bool.isRequired,
  resetShowError: PropTypes.func.isRequired,
};

export const EventParamSetting = withDefaultStyles(EventParamSettingUnstyled, getStepStyles);
